// The facts panel is a copy of packages/integrations/harbor/frontend/src/preamble.tsx,
// without the robot account's expiry: the p0-connector account's API token
// doesn't expire. The setup steps are Argo CD's own: the customer's Git change
// and the p0-connector account's API token.
import { ArgocdComponents } from "@p0security/integrations-argocd-shared/components";
import { isManagingApp } from "@p0security/integrations-argocd-shared/managing-app";
import {
  isNamespaceOrBlank,
  toNamespace,
} from "@p0security/integrations-argocd-shared/namespace";
import { findSecretNameProblem } from "@p0security/integrations-argocd-shared/secret-name";
import type { ArgocdInstanceConfig } from "@p0security/integrations-argocd-shared/types";
import { readVault } from "@p0security/integrations-argocd-shared/vault";
import type { FrontendInstallContext } from "@p0security/integrations-installer";
import { CommandDisplay, Typography } from "@p0security/ui";
import { useEffect, useState } from "react";

const { Paragraph, Text } = Typography;

/** What the instance's connector reports of it, as far as the installer
 * shows it. Each is there only when P0's answer held it as text. */
type InstanceFacts = {
  /** The connector's own setting for whose email may receive access */
  allowedEmailDomainPattern?: string;
  argoCdVersion?: string;
};

/** The states at which an instance's connector has checked it, so that P0
 * has facts to answer with. */
const CHECKED_STATES: readonly unknown[] = ["configure", "installed"];

const textOf = (value: unknown) =>
  typeof value === "string" && value !== "" ? value : undefined;

/** The facts in `body`, P0's answer to a read of the instance. The answer
 * comes from outside this page, so nothing about its shape is assumed. */
const readFacts = (body: unknown): InstanceFacts => {
  const metadata =
    typeof body === "object" && body !== null && "metadata" in body
      ? body.metadata
      : undefined;
  if (typeof metadata !== "object" || metadata === null) return {};
  const facts: Readonly<Record<string, unknown>> = { ...metadata };
  return {
    allowedEmailDomainPattern: textOf(facts.allowedEmailDomainPattern),
    argoCdVersion: textOf(facts.argoCdVersion),
  };
};

/** Shows what the connector reports of the Argo CD instance `id`: Argo CD's
 * version and the email domains the connector serves.
 *
 * P0 keeps neither, so they are read with the item each time. Nothing
 * renders until the read resolves, or when it fails or holds no facts: the
 * install can go on without them, and what is wrong is the verify step's to
 * say.
 */
const InstanceFactsPanel = ({
  authFetch,
  id,
}: Pick<FrontendInstallContext<unknown>, "authFetch"> & { id: string }) => {
  const [facts, setFacts] = useState<InstanceFacts>({});

  useEffect(() => {
    let cancelled = false;
    const fetchFacts = async (): Promise<InstanceFacts> => {
      try {
        const response = await authFetch(
          `integrations/argocd/config/argocd-instance/${encodeURIComponent(id)}`,
          { method: "GET" }
        );
        return readFacts(await response?.json());
      } catch {
        return {};
      }
    };
    void fetchFacts().then((read) => {
      if (!cancelled) setFacts(read);
    });
    return () => {
      cancelled = true;
    };
  }, [authFetch, id]);

  const { allowedEmailDomainPattern, argoCdVersion } = facts;
  if (!allowedEmailDomainPattern && !argoCdVersion) return undefined;
  return (
    <div>
      {argoCdVersion ? (
        <Paragraph>
          Argo CD version: <Text code>{argoCdVersion}</Text>
        </Paragraph>
      ) : undefined}
      {allowedEmailDomainPattern ? (
        <Paragraph>
          This instance&apos;s connector gives access only to people whose email
          domain matches <Text code>{allowedEmailDomainPattern}</Text>. The
          pattern is set in the connector&apos;s Terraform, and P0 can&apos;t
          change it.
        </Paragraph>
      ) : undefined}
    </div>
  );
};

/** The account P0's connector signs in to Argo CD as, and its role. Both are
 * fixed: the connector refuses any grant that names either. */
const ACCOUNT = "p0-connector";
const ROLE = `role:${ACCOUNT}`;

/** The customer's change to `argocd-cm`: a local account that has an API key
 * and can't log in.
 *
 * @internal exported for its tests */
export const renderArgoCdCm = () => `# argocd-cm
data:
  accounts.${ACCOUNT}: apiKey
`;

/**
 * The customer's change to `argocd-rbac-cm`: `email` in `scopes`, so that a
 * grant to an SSO user's email takes effect, and the connector's role.
 *
 * The role can read the Application that manages `argocd-rbac-cm`, update
 * that one ConfigMap through it (the resource-patch API, which is how the
 * connector writes `policy.p0.csv`), and read projects, to list their roles.
 * Nothing else.
 *
 * @param args.app The managing Application, as `<project>/<application>`
 * @param args.namespace The namespace `argocd-rbac-cm` is in
 * @internal exported for its tests
 */
export const renderRbacCm = (args: { app: string; namespace: string }) => {
  const { app, namespace } = args;
  return `# argocd-rbac-cm, in the namespace ${namespace}
data:
  # Keep the scopes you have, and add email to them.
  scopes: "[groups, email]"
  # Add these lines to your own policy.csv, or to another policy key of yours.
  # Never to policy.p0.csv: P0's connector owns that key.
  policy.csv: |
    p, ${ROLE}, applications, get, ${app}, allow
    p, ${ROLE}, applications, update//ConfigMap/${namespace}/argocd-rbac-cm, ${app}, allow
    p, ${ROLE}, projects, get, *, allow
    g, ${ACCOUNT}, ${ROLE}
`;
};

/** Where the token goes, as the command that stores it names it. */
type TokenVault =
  | { type: "aws-sm"; region: string; secretName: string }
  | { type: "gcp-sm"; project: string; secretName: string };

/**
 * The command that creates the `p0-connector` account's API token and stores
 * it as the JSON `{"token": "<API token>"}`, which the connector reads.
 *
 * The token goes from `argocd` through `jq` into the secret manager, so it's
 * never on the screen, in a file or in the shell's history. With no
 * `--expires-in`, the token doesn't expire. Each value has passed a rule that
 * admits nothing the shell would read as more than one word.
 *
 * @internal exported for its tests
 */
export const renderTokenCommand = (vault: TokenVault) => {
  const store =
    vault.type === "aws-sm"
      ? `aws secretsmanager create-secret --name ${vault.secretName} --region ${vault.region} --secret-string file:///dev/stdin`
      : `gcloud secrets create ${vault.secretName} --project ${vault.project} --data-file=-`;
  return `argocd account generate-token --account ${ACCOUNT} \\
  | jq -Rc '{token: .}' \\
  | ${store}
`;
};

/** What the setup steps read of the instance. On the page of an instance
 * that P0 stored long ago, or by another rule, any of it may be missing or
 * unusable. */
type StoredInstance = Readonly<
  Partial<
    Pick<
      ArgocdInstanceConfig,
      "managingApp" | "namespace" | "secretName" | "state" | "vault"
    >
  >
>;

const PLACEHOLDERS = {
  app: "<project>/<application>",
  namespace: "<namespace>",
  project: "<project>",
  region: "<region>",
  secretName: "<secret name>",
} as const;

/** `value`, trimmed, if it passes `test`; otherwise `placeholder`. No value
 * of the item is shown in a snippet before it passes a rule: this one, or
 * `readVault`'s for the vault. */
const valueOr = (
  value: unknown,
  test: (trimmed: string) => boolean,
  placeholder: string
) => {
  if (typeof value !== "string") return placeholder;
  const trimmed = value.trim();
  return test(trimmed) ? trimmed : placeholder;
};

/** The vaults the token command is shown for: the instance's, or both when
 * it names none. A vault that `readVault` refuses keeps its type, with
 * placeholders for its values. */
const toTokenVaults = (item: StoredInstance | undefined): TokenVault[] => {
  const type = item?.vault?.type;
  const read = readVault(item?.vault);
  const vault = "vault" in read ? read.vault : undefined;
  const aws: TokenVault = {
    region:
      vault?.type === "aws-sm" ? vault.secretsRegion : PLACEHOLDERS.region,
    secretName: valueOr(
      item?.secretName,
      (name) =>
        findSecretNameProblem(name, { by: "name", vault: "aws-sm" }) ===
        undefined,
      PLACEHOLDERS.secretName
    ),
    type: "aws-sm",
  };
  const gcp: TokenVault = {
    project: vault?.type === "gcp-sm" ? vault.install : PLACEHOLDERS.project,
    secretName: valueOr(
      item?.secretName,
      (name) => findSecretNameProblem(name, { vault: "gcp-sm" }) === undefined,
      PLACEHOLDERS.secretName
    ),
    type: "gcp-sm",
  };
  if (type === "aws-sm") return [aws];
  if (type === "gcp-sm") return [gcp];
  return [aws, gcp];
};

const { vault: vaultElement } = ArgocdComponents["argocd-instance"].schema;

/** A vault, as the instance's vault field names it. */
const vaultLabel = (type: TokenVault["type"]) =>
  vaultElement.options[type].label;

const VAULT_CLIS = {
  "aws-sm": "the AWS CLI signed in to the secret's account",
  "gcp-sm": "gcloud signed in with access to the secret's project",
} as const;

/** The managing Application and namespace that the role's lines name: the
 * instance's, where each passes its rule, and placeholders otherwise. A
 * namespace left out or blank is Argo CD's default.
 *
 * @internal exported for its tests */
export const toPolicyTarget = (item: StoredInstance | undefined) => ({
  app: valueOr(item?.managingApp, isManagingApp, PLACEHOLDERS.app),
  namespace: isNamespaceOrBlank(item?.namespace ?? "")
    ? toNamespace({ namespace: item?.namespace })
    : PLACEHOLDERS.namespace,
});

/** What the customer does in Argo CD before the instance can be checked: the
 * Git change that adds the p0-connector account, and the account's API
 * token, filled in from the instance where its values can be. */
const SetupSteps = ({ item }: { item: StoredInstance | undefined }) => {
  const { app, namespace } = toPolicyTarget(item);
  const vaults = toTokenVaults(item);
  // The instance's own vault, when it names one.
  const instanceVault = vaults.length === 1 ? vaults[0].type : undefined;
  const where = instanceVault
    ? vaultLabel(instanceVault)
    : `${vaultLabel("aws-sm")} or ${vaultLabel("gcp-sm")}, whichever the instance names,`;
  return (
    <div>
      <Paragraph>
        First, in the Git repository Argo CD&apos;s configuration is synced
        from, add the <Text code>{ACCOUNT}</Text> account, which P0&apos;s
        connector signs in as, and let Argo CD sync the change. The account can
        read the Application <Text code>{app}</Text>, which manages
        argocd-rbac-cm, update argocd-rbac-cm through it, and read projects.
      </Paragraph>
      <CommandDisplay commands={renderArgoCdCm()} />
      <CommandDisplay commands={renderRbacCm({ app, namespace })} />
      <Paragraph>
        <Text strong type="danger">
          Never add the policy.p0.csv key to Git.
        </Text>{" "}
        P0&apos;s connector writes every grant there, and a sync would replace
        them with what Git holds.{" "}
        <Text strong type="danger">
          Never sync argocd-rbac-cm with Replace=true.
        </Text>{" "}
        A replace sync deletes policy.p0.csv, and with it every grant.
      </Paragraph>
      <Paragraph>
        Then, signed in to Argo CD with the argocd CLI as an admin, create the
        account&apos;s API token. This command creates one that doesn&apos;t
        expire and stores it in {where} as the JSON{" "}
        <Text code>{'{"token": "<API token>"}'}</Text>, without showing it. It
        needs jq, and{" "}
        {vaults.map((vault) => VAULT_CLIS[vault.type]).join(" or ")}.
        {instanceVault === "aws-sm"
          ? " For a secret in another account than the connector's, add --kms-key-id with the ARN of a KMS key of your own: the Terraform below needs one."
          : ""}{" "}
        If the command fails after argocd has run, delete the token it created:
        find its ID with{" "}
        <Text code>argocd account get --account p0-connector</Text>, then run{" "}
        <Text code>
          argocd account delete-token --account p0-connector &lt;ID&gt;
        </Text>
        .
      </Paragraph>
      {vaults.map((vault) => (
        <CommandDisplay commands={renderTokenCommand(vault)} key={vault.type} />
      ))}
    </div>
  );
};

/** The instance page's preamble: until the instance's connector has checked
 * it, the changes to make in Argo CD; after, what the connector reports of
 * it. */
export const instancePreamble = (
  context: Pick<FrontendInstallContext<unknown>, "authFetch">,
  id: string,
  item: StoredInstance | undefined
) =>
  CHECKED_STATES.includes(item?.state) ? (
    <InstanceFactsPanel authFetch={context.authFetch} id={id} />
  ) : (
    <SetupSteps item={item} />
  );
